import { forwardRef, ReactNode } from 'react';
import { Divider, DividerProps, useTheme } from '@mui/material';
import { SlotProvider } from '@upheal/design-system/utils/SlotContext';
import { Slots } from '@upheal/design-system/utils/Slots';

type ContentDividerContrast = 'subtle' | 'strong';

interface ContentDividerProps extends Omit<DividerProps, 'textAlign'> {
    /**
     * Determines the visual strength of the divider line
     * @default 'subtle'
     */
    contrast?: ContentDividerContrast;

    /**
     * Optional content displayed in the middle of the divider line
     */
    children?: ReactNode;
}

const slots: Slots = {
    button: {
        size: 'xs',
        variant: 'outline',
        colorScheme: 'neutral',
    },
};

/**
 * ContentDivider component for creating visual separations with optional content
 * breaking the line.
 */
export const ContentDivider = forwardRef<HTMLHRElement, ContentDividerProps>(
    ({ contrast = 'subtle', sx, ...dividerProps }, ref) => {
        const theme = useTheme();
        const borderColor =
            theme.palette.ds.border.neutral[
                contrast === 'strong' ? 'primary' : 'secondary'
            ];
        const color =
            theme.palette.ds.fg.neutral[
                contrast === 'strong' ? 'primary' : 'tertiary'
            ];

        return (
            <SlotProvider value={slots}>
                <Divider
                    textAlign="center"
                    ref={ref}
                    {...dividerProps}
                    sx={{
                        /**
                         * Override SUI styleOverrides from https://github.com/Upheal/upheal-app/blob/master/libs/design-system/src/lib/theme/legacy/components/divider.js
                         * Can be removed once we get rid of those overrides.
                         */
                        borderBottom: 'thin solid',
                        backgroundImage: 'unset !important',
                        backgroundColor: 'unset',
                        margin: 'unset',
                        opacity: 1,

                        '&.MuiDivider-vertical': {
                            borderBottom: 'none',
                            borderRight: 'thin solid',
                            borderColor,
                        },

                        '&.MuiDivider-withChildren': {
                            height: 'unset',
                            borderBottom: 'none',
                            borderRight: 'none',
                        },

                        '&.MuiDivider-withChildren.MuiDivider-vertical': {
                            width: 'unset',
                        },
                        /**
                         * SUI styleOverrides end
                         */

                        borderColor,
                        color,
                        ...theme.typography['ds.xs'],
                        ...sx,

                        '&:after, &:before': {
                            borderColor,
                        },
                    }}
                />
            </SlotProvider>
        );
    },
);
